import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePromptDialog } from '../hooks/usePromptDialog';
import { usePlatformAccessLevel } from '../features/platformPermissions/usePlatformAccessLevel';
import {
  createDesignProject,
  escalateDesignProject,
  fetchDesignProjects,
  transitionDesignStage,
} from '../features/design-development/designProjectsApi';
import type { DesignProjectSummary, DesignStageSummary } from '../features/design-development/types';
import {
  PROJECT_STATUS_LABEL,
  STAGE_KIND_LABEL,
  STAGE_STATUS_LABEL,
  projectStatusBadgeClass,
  stageStatusBadgeClass,
} from '../features/design-development/designUtils';

export function DesignProjectsPage() {
  const { prompt } = usePromptDialog();
  const { canEdit } = usePlatformAccessLevel('design_development.access');
  const [items, setItems] = useState<DesignProjectSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await fetchDesignProjects({
        page,
        pageSize: 20,
        search: search.trim() || undefined,
        sortBy: 'createdAt',
        sortDir: 'desc',
      });
      setItems(list.data);
      setTotalPages(list.pagination.pages);
    } catch {
      setError('No se pudieron cargar los proyectos de diseño.');
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCreate = async () => {
    if (!title.trim()) return;
    await createDesignProject({ title, description: description || null });
    setCreateOpen(false);
    setTitle('');
    setDescription('');
    await load();
  };

  const approveStage = async (project: DesignProjectSummary, stage: DesignStageSummary) => {
    const resultNote = await prompt({
      title: 'Aprobar etapa',
      message: 'Indique el resultado de la etapa. Es obligatorio para aprobar.',
      label: 'Resultado de la etapa',
      confirmText: 'Aprobar',
      multiline: true,
      required: true,
    });
    if (!resultNote) return;
    await transitionDesignStage(project.id, stage.id, {
      status: 'aprobado',
      resultNote,
    });
    await load();
  };

  const rejectStage = async (project: DesignProjectSummary, stage: DesignStageSummary) => {
    await transitionDesignStage(project.id, stage.id, { status: 'rechazado' });
    await load();
  };

  const escalate = async (item: DesignProjectSummary) => {
    await escalateDesignProject(item.id);
    await load();
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Diseño y desarrollo</h1>
          <p className="text-muted-foreground text-sm">ISO 9001 §8.3 — etapas de revisión, verificación y validación</p>
        </div>
        {canEdit && (
          <button type="button" className="btn btn-primary" onClick={() => setCreateOpen(true)}>
            Nuevo proyecto
          </button>
        )}
      </div>

      <input
        type="search"
        placeholder="Buscar por código o título…"
        className="rounded-md border border-border px-3 py-2 text-sm min-w-[200px]"
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setPage(1);
        }}
      />

      {error && <p className="text-red-600 text-sm">{error}</p>}
      {loading && <p className="text-muted-foreground text-sm">Cargando…</p>}

      {!loading && items.length === 0 && (
        <p className="text-muted-foreground text-sm">No hay proyectos de diseño registrados.</p>
      )}

      <div className="space-y-4">
        {items.map((item) => (
          <article key={item.id} className="rounded-lg border border-border p-4 space-y-3">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="font-mono text-xs text-muted-foreground">{item.code}</p>
                <h2 className="text-lg font-semibold">{item.title}</h2>
                {item.description && (
                  <p className="text-sm text-muted-foreground mt-1">{item.description}</p>
                )}
              </div>
              <span className={`text-xs px-2 py-1 rounded-full ${projectStatusBadgeClass(item.status)}`}>
                {PROJECT_STATUS_LABEL[item.status]}
              </span>
            </div>

            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
              {item.stages.map((stage) => (
                <div key={stage.id} className="rounded border border-border/60 p-3 text-sm space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium">{STAGE_KIND_LABEL[stage.kind]}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${stageStatusBadgeClass(stage.status)}`}>
                      {STAGE_STATUS_LABEL[stage.status]}
                    </span>
                  </div>
                  {stage.resultNote && (
                    <p className="text-xs text-muted-foreground">{stage.resultNote}</p>
                  )}
                  {canEdit && stage.status === 'en_progreso' && (
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={() => void approveStage(item, stage)}
                      >
                        Aprobar
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline"
                        onClick={() => void rejectStage(item, stage)}
                      >
                        Rechazar
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-2 text-sm">
              {item.bridgeRecordId ? (
                <Link
                  className="text-primary underline"
                  to={`/registros/no-conformidades/${item.bridgeRecordId}`}
                >
                  Ver NC vinculada
                </Link>
              ) : (
                canEdit && (
                  <button type="button" className="text-primary underline" onClick={() => void escalate(item)}>
                    Escalar a CAPA
                  </button>
                )
              )}
            </div>
          </article>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="flex gap-2">
          <button
            type="button"
            className="btn btn-outline btn-sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Anterior
          </button>
          <span className="text-sm self-center">
            Página {page} de {totalPages}
          </span>
          <button
            type="button"
            className="btn btn-outline btn-sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Siguiente
          </button>
        </div>
      )}

      {createOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-background rounded-lg border border-border p-6 w-full max-w-lg space-y-4">
            <h3 className="text-lg font-semibold">Nuevo proyecto de diseño</h3>
            <input
              className="w-full rounded-md border border-border px-3 py-2 text-sm"
              placeholder="Título"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <textarea
              className="w-full rounded-md border border-border px-3 py-2 text-sm min-h-[100px]"
              placeholder="Descripción (opcional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <button type="button" className="btn btn-outline" onClick={() => setCreateOpen(false)}>
                Cancelar
              </button>
              <button type="button" className="btn btn-primary" onClick={() => void handleCreate()}>
                Crear
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
